import { useState, useRef, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Star, X, CaretDown } from "@phosphor-icons/react"
import { usePinnedPagesStore } from "../../stores"
import { cn } from "../../lib/utils"

export function PinnedPagesDropdown() {
  const navigate = useNavigate()
  const { pinnedPages, removePin } = usePinnedPagesStore()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Disambiguate duplicate labels with sequential numbers
  const displayLabels = useMemo(() => {
    const labelCounts = new Map<string, number>()
    for (const page of pinnedPages) {
      labelCounts.set(page.label, (labelCounts.get(page.label) || 0) + 1)
    }
    const seen = new Map<string, number>()
    return pinnedPages.map((page) => {
      if ((labelCounts.get(page.label) || 0) > 1) {
        const num = (seen.get(page.label) || 0) + 1
        seen.set(page.label, num)
        return `${page.label} ${num}`
      }
      return page.label
    })
  }, [pinnedPages])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  const handleRemove = (e: React.MouseEvent, path: string) => {
    e.stopPropagation()
    removePin(path)
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm font-medium transition-colors",
          pinnedPages.length > 0
            ? "text-amber-600 dark:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10"
            : "text-muted-foreground hover:bg-muted",
          isOpen && "bg-accent"
        )}
        title="Pinned pages"
      >
        <Star className="h-4 w-4" weight={pinnedPages.length > 0 ? "fill" : "regular"} />
        <span className="hidden lg:inline">Pinned</span>
        {pinnedPages.length > 0 && (
          <span className="hidden lg:inline text-xs bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
            {pinnedPages.length}
          </span>
        )}
        <CaretDown
          className={cn(
            "h-3 w-3 hidden lg:block transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 top-full mt-2 w-64 rounded-xl",
            "bg-background border border-border shadow-xl",
            "overflow-hidden z-50",
            "animate-flyout-enter"
          )}
        >
          {pinnedPages.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <Star className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No pinned pages yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Click the star icon next to a page title to pin it
              </p>
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Pinned Pages
                </p>
              </div>
              <div className="py-1 max-h-[300px] overflow-y-auto">
                {pinnedPages.map((page, index) => (
                  <button
                    key={page.path}
                    onClick={() => handleNavigate(page.path)}
                    className="group flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {displayLabels[index]}
                      </p>
                      {page.section && (
                        <p className="text-xs text-muted-foreground truncate">
                          {page.section}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleRemove(e, page.path)}
                      className="opacity-0 group-hover:opacity-100 ml-2 p-1 rounded hover:bg-accent transition-all"
                      title="Remove pin"
                    >
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
